import React, { useContext, useEffect } from "react";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/shadcn/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/shadcn/form";
import { Input } from "@/components/shadcn/input";

import { useTranslation } from "react-i18next";
import { saveSettings } from "@/store/chatbot.ts";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card.tsx";
import Loading from "@/components/Loading";
import { Switch } from "@/components/shadcn/switch.tsx";

const FormSchema = z.object({
  enable: z.boolean(),
  token: z.string().or(z.literal("")),
});

export default function TelegramIntegration() {
  const { chatbotID } = useParams();
  const { t } = useTranslation();
  const { chatSettings, viewChatbot, invalidateChatbot, setSettings } =
    useContext(ChatbotContext);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    let set = Object.assign({}, chatSettings);
    set.integrations.telegramBot = data;
    saveSettings(chatbotID, set, invalidateChatbot);
  }

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      enable: chatSettings?.integrations.telegramBot.enable,
      token: chatSettings?.integrations.telegramBot.token,
    },
  });

  useEffect(() => {
    if (chatSettings) {
      form.reset(chatSettings?.integrations.telegramBot)
    }
  }, [chatSettings]);

  if (!viewChatbot || !chatSettings) {
    return <Loading />;
  }

  return (
    <Card className="">
      <CardHeader>
        <CardTitle>Telegram Bot</CardTitle>
        <CardDescription></CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-6"
          >
            <FormField
              control={form.control}
              name="enable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="cursor-pointer">
                      {t("integrations.enable_integration")}
                    </FormLabel>
                    <FormDescription>
                      {t("integrations.telegram.enable")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telegram Token</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: t("integrations.telegram.token.description"),
                      }}
                    />
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit">{t("save")}</Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between"></CardFooter>
    </Card>
  );
}
